/**
 * On-scroll reveal.
 *
 * Every `#MainContent` section that starts below the fold gets `.scroll-reveal` and fades/slides
 * in when it enters the viewport. Card-like children (grid items, carousel slides, accordion
 * rows, anything inside `[data-reveal-stagger]`) get `.scroll-reveal-item` + `--reveal-index`
 * so they follow one after another once their section is revealed.
 *
 * Sections already on screen at load are left alone, so there is no flash of hidden content.
 * Elements can still opt in manually with class="scroll-reveal" (and optional --reveal-index).
 */

const SECTION_SELECTOR = '#MainContent > .shopify-section';
const STAGGER_SELECTOR = [
  '.product-grid__item',
  '.resource-list__item',
  '.carousel-content slideshow-slide',
  '.accordion > .details',
  '[data-reveal-stagger] > *',
].join(',');
const MAX_STAGGER = 8;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) reveal(entry.target);
    });
  },
  { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
);

/** @param {Element} element */
function reveal(element) {
  element.classList.add('is-visible');
  observer.unobserve(element);
}

/** @param {Element} section */
function prepareSection(section) {
  if (section.classList.contains('scroll-reveal')) return;
  if (section.getBoundingClientRect().top < window.innerHeight) return;

  section.classList.add('scroll-reveal');

  const groups = new Map();
  section.querySelectorAll(STAGGER_SELECTOR).forEach((item) => {
    const parent = item.parentElement;
    const index = groups.get(parent) ?? 0;
    groups.set(parent, index + 1);

    item.classList.add('scroll-reveal-item');
    if (item instanceof HTMLElement) {
      item.style.setProperty('--reveal-index', String(Math.min(index, MAX_STAGGER)));
    }
  });
}

/** @param {ParentNode} [root] */
function observeAll(root = document) {
  if (reducedMotion.matches) return;

  if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
    prepareSection(root);
  } else {
    root.querySelectorAll(SECTION_SELECTOR).forEach(prepareSection);
  }

  const targets = root instanceof Element && root.matches('.scroll-reveal') ? [root] : [];
  targets.push(...root.querySelectorAll('.scroll-reveal:not(.is-visible)'));
  targets.forEach((element) => observer.observe(element));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => observeAll());
} else {
  observeAll();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) observeAll(event.target);
});

// Never leave a section hidden while a merchant is editing it in the theme editor.
document.addEventListener('shopify:section:select', (event) => {
  if (event.target instanceof Element) event.target.querySelectorAll('.scroll-reveal').forEach(reveal);
  if (event.target instanceof Element && event.target.matches('.scroll-reveal')) reveal(event.target);
});
